import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/ui/Toast';
import { useAuth } from './hooks/useAuth';
import { ProtectedRoute } from './components/ProtectedRoute';
import { registerServiceWorker } from './lib/pushNotifications';
import { Login } from './pages/auth/Login';
import { ChangePassword } from './pages/auth/ChangePassword';
import { Dashboard } from './pages/dashboard/Dashboard';
import { Products } from './pages/products/Products';
import { ProductForm } from './pages/products/ProductForm';
import { Orders } from './pages/orders/Orders';
import { Earnings } from './pages/earnings/Earnings';
import { Profile } from './pages/profile/Profile';
import StorefrontPreview from './storefront/StorefrontPreview';

function App() {
  // Register the service worker once on app start. Idempotent — the
  // browser dedupes by URL — so reloads + remounts are fine. Push
  // notifications won't deliver without an active SW even when the
  // server-side subscription is registered, so this is the gate.
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<PublicOnly element={<Login />} />} />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <Products />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/new"
              element={
                <ProtectedRoute>
                  <ProductForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products/:id/edit"
              element={
                <ProtectedRoute>
                  <ProductForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/earnings"
              element={
                <ProtectedRoute>
                  <Earnings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            {/* Public: a design surface with no vendor data behind it, so
                gating it behind auth would only make it harder to review. */}
            <Route path="/storefront/preview" element={<StorefrontPreview />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}

/** Public auth pages — bounce already-logged-in users to /dashboard
 *  (or /change-password if they still owe a rotation). */
const PublicOnly: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { isAuthenticated, mustChangePassword, isLoading } = useAuth();
  if (isLoading) return null;
  if (isAuthenticated) {
    return (
      <Navigate
        to={mustChangePassword ? '/change-password' : '/dashboard'}
        replace
      />
    );
  }
  return element;
};

export default App;
